import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, OctagonAlert } from 'lucide-react';

/**
 * Styled confirmation dialog for delete actions.
 *
 * Props (all optional except open / onOpenChange / onConfirm):
 *   itemName        - shown in default body copy
 *   loading         - disables buttons while an operation is in-flight
 *   variant         - 'warning' (default, amber triangle) or 'critical'
 *                     (red octagon) — use critical for destructive-cascade
 *                     actions like "delete schedule + all its grades"
 *   title           - overrides the default title
 *   prompt          - overrides the default body copy (string or JSX)
 *   confirmLabel    - button copy for the confirm action
 *   cancelLabel     - button copy for the cancel action
 */
export function DeleteConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  itemName = 'this record',
  loading = false,
  variant = 'warning',
  title,
  prompt,
  confirmLabel,
  cancelLabel,
}) {
  const isCritical = variant === 'critical';
  const Icon = isCritical ? OctagonAlert : AlertTriangle;
  const titleColor = isCritical ? 'text-red-700' : 'text-amber-700';

  const resolvedTitle = title || (isCritical
    ? 'Do you want to delete this?'
    : "You've asked to delete this record?");
  const resolvedPrompt = prompt || (
    <>
      <p className="text-stone-700">Are you SURE you want to delete {itemName}?</p>
      <p className="text-sm text-stone-500 mt-2">This action cannot be undone.</p>
    </>
  );
  const resolvedConfirm = confirmLabel || (isCritical ? 'Yes' : 'Yes, Delete');
  const resolvedCancel = cancelLabel || (isCritical ? 'No' : 'Cancel');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${titleColor}`}>
            <Icon className="h-5 w-5" />
            {resolvedTitle}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">{resolvedPrompt}</div>
        <div className="flex gap-3">
          <Button
            onClick={() => onOpenChange(false)}
            variant="outline"
            className="flex-1"
            disabled={loading}
          >
            {resolvedCancel}
          </Button>
          <Button
            onClick={onConfirm}
            variant="destructive"
            className="flex-1 bg-red-600 hover:bg-red-700"
            disabled={loading}
            data-testid="confirm-delete-button"
          >
            {loading ? 'Deleting...' : resolvedConfirm}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
